import { normalize } from './recipes';

const SEARCH_STOP_WORDS = new Set([
  'af',
  'den',
  'det',
  'en',
  'et',
  'eller',
  'for',
  'fra',
  'i',
  'med',
  'og',
  'paa',
  'som',
  'til',
  'uden',
  'ved'
]);

function normalizeSearchText(value) {
  return normalize(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function tokenize(value) {
  const normalized = normalizeSearchText(value);
  return normalized ? normalized.split(/\s+/).filter(Boolean) : [];
}

function getQueryTokens(value) {
  const tokens = tokenize(value);
  const meaningfulTokens = tokens.filter((token) => !SEARCH_STOP_WORDS.has(token));
  return meaningfulTokens.length > 0 ? meaningfulTokens : tokens;
}

function getLevenshteinDistance(left, right) {
  if (left === right) {
    return 0;
  }

  if (!left.length) {
    return right.length;
  }

  if (!right.length) {
    return left.length;
  }

  let previousRow = Array.from({ length: right.length + 1 }, (_, index) => index);

  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    const currentRow = [leftIndex + 1];

    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const substitutionCost = left[leftIndex] === right[rightIndex] ? 0 : 1;
      currentRow.push(Math.min(
        currentRow[rightIndex] + 1,
        previousRow[rightIndex + 1] + 1,
        previousRow[rightIndex] + substitutionCost
      ));
    }

    previousRow = currentRow;
  }

  return previousRow[right.length];
}

function tokenMatches(queryToken, candidateToken) {
  if (candidateToken === queryToken || candidateToken.startsWith(queryToken)) {
    return true;
  }

  if (queryToken.length >= 4 && candidateToken.includes(queryToken)) {
    return true;
  }

  if (queryToken.length < 5) {
    return false;
  }

  const maxDistance = queryToken.length >= 7 ? 2 : 1;
  if (Math.abs(queryToken.length - candidateToken.length) > maxDistance) {
    return false;
  }

  return getLevenshteinDistance(queryToken, candidateToken) <= maxDistance;
}

export function buildRecipeSearchTokens(recipe) {
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const searchableValues = [
    recipe?.name,
    recipe?.title,
    recipe?.ret_type,
    recipe?.maaltidsprofil,
    ...ingredients.flatMap((ingredient) => [
      ingredient?.produkt_navn,
      ingredient?.produkt_kategori,
      ingredient?.ingredient_family_name,
      ingredient?.allowed_variants,
      ingredient?.erstatning_1,
      ingredient?.erstatning_2
    ])
  ];

  return Array.from(new Set(searchableValues.flatMap(tokenize)));
}

export function matchesRecipeSearch(searchTokens, query) {
  const queryTokens = getQueryTokens(query);

  if (queryTokens.length === 0) {
    return true;
  }

  const candidateTokens = Array.isArray(searchTokens) ? searchTokens : [];
  return queryTokens.every((queryToken) =>
    candidateTokens.some((candidateToken) => tokenMatches(queryToken, candidateToken))
  );
}

export function compareRecipesByPantryPrice(left, right) {
  const getPrice = (card) => {
    const rawPantryPrice = card?.priceComparison?.pantryWeeklyValue;
    const pantryPrice = Number(rawPantryPrice);
    if (rawPantryPrice !== null && rawPantryPrice !== undefined && rawPantryPrice !== '' && Number.isFinite(pantryPrice)) {
      return pantryPrice;
    }

    const rawWeeklyPrice = card?.weeklyPrice;
    const weeklyPrice = Number(rawWeeklyPrice);
    return rawWeeklyPrice !== null && rawWeeklyPrice !== undefined && rawWeeklyPrice !== '' && Number.isFinite(weeklyPrice)
      ? weeklyPrice
      : Number.POSITIVE_INFINITY;
  };
  const leftPrice = getPrice(left);
  const rightPrice = getPrice(right);

  if (leftPrice !== rightPrice) {
    return leftPrice < rightPrice ? -1 : 1;
  }

  const leftSavings = Number(left?.priceComparison?.pantrySavingsValue) || 0;
  const rightSavings = Number(right?.priceComparison?.pantrySavingsValue) || 0;
  return rightSavings - leftSavings;
}
